import { WherebyCallKind } from '../../../../pure-js/enums/Whereby'

// Same-browser coordination for the gamer game call (BroadcastChannel).
//
// Two jobs:
//   1. Drop a stale game tab when the game is OVER (leave-game-call) — a forgotten tab must not keep the
//      game room alive and block the AI coach transcript.
//   2. Hand out a stable per-tab id (`getWherebyTabId`) used as the `clientId` of the one-call-per-user
//      ownership claim (see useWherebyCallOwnership).
//
// It deliberately does NOT decide who owns the call: a tab that wants the call asks the server and the
// user confirms. See `pure-js/libs/wherebyCallOwnership.ts`.

const WHEREBY_CROSS_TAB_CHANNEL = 'whereby-call-coordination'
const WHEREBY_TAB_ID_CHANNEL = 'whereby-tab-id'
const WHEREBY_TAB_ID_STORAGE_KEY = 'whereby_tab_id'

const createTabId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  } catch {
    // fall through
  }
  return `tab-${Date.now()}-${Math.floor(Math.random() * 1e9)}`
}

const readStoredTabId = (): string | null => {
  try {
    return sessionStorage.getItem(WHEREBY_TAB_ID_STORAGE_KEY)
  } catch {
    return null
  }
}

const storeTabId = (id: string): void => {
  try {
    sessionStorage.setItem(WHEREBY_TAB_ID_STORAGE_KEY, id)
  } catch {
    // private mode / storage blocked — the in-memory id still works for this page load.
  }
}

// Persisted in sessionStorage so a RELOAD keeps the same id: the reloaded tab then re-claims the call as
// itself instead of being told "your call is open somewhere else".
let tabId = readStoredTabId() ?? createTabId()
storeTabId(tabId)

// A late "in use" answer — a suspended sibling waking minutes after our ping — must not rewrite a live
// tab's identity: the tab would then be blocked by its own still-heartbeating claim.
const TAB_ID_REMINT_WINDOW_MS = 5000 // 5 s
const moduleBootAtMs = Date.now()

// "Duplicate tab" COPIES sessionStorage, so two tabs can boot with the same id — and the server would
// treat the copy as the original and let both connect. On load we ask siblings whether our id is already
// taken; whoever gets an answer mints a fresh one. (BroadcastChannel never echoes to the sender, so the
// original tab answers and keeps its id.)
if (typeof BroadcastChannel !== 'undefined') {
  try {
    const channel = new BroadcastChannel(WHEREBY_TAB_ID_CHANNEL)
    channel.onmessage = (event: MessageEvent) => {
      const message = event.data as { type?: string; tabId?: string } | undefined
      if (!message || message.tabId !== tabId) return
      if (message.type === 'tab-id-ping') channel.postMessage({ type: 'tab-id-in-use', tabId })
      if (message.type === 'tab-id-in-use' && Date.now() - moduleBootAtMs < TAB_ID_REMINT_WINDOW_MS) {
        tabId = createTabId()
        storeTabId(tabId)
      }
    }
    channel.postMessage({ type: 'tab-id-ping', tabId })
  } catch {
    // No cross-tab coordination available; ignore.
  }
}

/** Stable id for THIS browser tab (survives reload, unique across duplicated tabs). */
export const getWherebyTabId = (): string => tabId

export interface WherebyLeaveGameMessage {
  type: 'leave-game-call'
  teamId?: string
  sprintId?: string
  activityId?: string
}

/** A confirmed takeover: sibling tabs of this browser drop the call now, not on the next Firestore push.
 * Carries the user id — the claim is per user, and a browser can hold different accounts in different tabs. */
export interface WherebyOwnershipTakenMessage {
  type: 'call-ownership-taken'
  userId: string
  newClientId: string
}

export type WherebyCrossTabMessage = WherebyLeaveGameMessage | WherebyOwnershipTakenMessage

interface LeaveDecisionState {
  isCallActive: boolean
  callScopeKind?: string
  callScopeTeamId?: string
}

// Should THIS tab drop its game call for a sibling's game-over signal? Only a live game call for the same
// team. This is NOT a takeover — the game has ended, so nobody's live conversation is cut short.
export const shouldLeaveGameForMessage = (state: LeaveDecisionState, message: WherebyLeaveGameMessage): boolean => {
  if (message.type !== 'leave-game-call') return false
  if (!state.isCallActive || state.callScopeKind !== WherebyCallKind.GAME) return false
  if (message.teamId && state.callScopeTeamId && message.teamId !== state.callScopeTeamId) return false
  return true
}

const supportsBroadcastChannel = (): boolean => typeof BroadcastChannel !== 'undefined'

const postMessage = (message: WherebyCrossTabMessage): void => {
  if (!supportsBroadcastChannel()) return
  try {
    const channel = new BroadcastChannel(WHEREBY_CROSS_TAB_CHANNEL)
    channel.postMessage(message)
    channel.close()
  } catch {
    // No cross-tab coordination available; ignore.
  }
}

// Tell sibling tabs to leave their stale game call (best-effort).
export const broadcastLeaveGameCall = (scope?: { teamId?: string; sprintId?: string; activityId?: string }): void => {
  postMessage({ type: 'leave-game-call', ...scope })
}

// Tell sibling tabs the user moved the call here (best-effort; the Firestore watch is the backstop).
export const broadcastCallOwnershipTaken = (userId: string, newClientId: string): void => {
  postMessage({ type: 'call-ownership-taken', userId, newClientId })
}

const CROSS_TAB_MESSAGE_TYPES = new Set<WherebyCrossTabMessage['type']>(['leave-game-call', 'call-ownership-taken'])

// Subscribe to sibling call signals. Returns an unsubscribe fn.
export const subscribeToCrossTabMessages = (handler: (message: WherebyCrossTabMessage) => void): (() => void) => {
  if (!supportsBroadcastChannel()) return () => {}
  let channel: BroadcastChannel | undefined
  try {
    channel = new BroadcastChannel(WHEREBY_CROSS_TAB_CHANNEL)
    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as WherebyCrossTabMessage | undefined
      if (data && CROSS_TAB_MESSAGE_TYPES.has(data.type)) handler(data)
    }
  } catch {
    return () => {}
  }
  return () => {
    try {
      if (channel) {
        channel.onmessage = null
        channel.close()
      }
    } catch {
      // ignore
    }
  }
}
